import { AskStanleyRequest, AskStanleyResponse, sendAskStanleyMessage } from '@/lib/api/askStanley';
import { ChatMessage, ProductData } from '../types/askStanley.types';
import { ASK_STANLEY_CONSTANTS } from '../constants/askStanley.constants';
import { marked } from 'marked';
import { sanitizeHtml } from '@/lib';

// Configure marked to not convert single line breaks to <br> tags
marked.setOptions({
    breaks: false, // Don't convert single line breaks to <br>
    gfm: true, // GitHub Flavored Markdown
});

/**
 * Post-process HTML to remove excessive <br> tags
 * Removes <br> tags that appear after block-level elements
 */
function cleanExcessiveBreaks(html: string): string {
    // Remove <br> tags that appear immediately after block-level elements
    // This regex matches <br> tags that come after closing tags like </p>, </h1-6>, </ul>, </ol>, </li>, </div>, etc.
    return (
        html
            .replace(/(<\/?(?:p|h[1-6]|ul|ol|li|div|blockquote|pre|hr)[^>]*>)\s*<br\s*\/?>\s*/gi, '$1\n')
            .replace(/<br\s*\/?>\s*(<[/]?(?:p|h[1-6]|ul|ol|li|div|blockquote|pre|hr)[^>]*>)/gi, '\n$1')
            // Remove multiple consecutive <br> tags (more than 2)
            .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
            // Remove <br> tags at the start or end of block elements
            .replace(/(<(?:p|h[1-6]|ul|ol|li|div)[^>]*>)\s*<br\s*\/?>\s*/gi, '$1')
            .replace(/\s*<br\s*\/?>\s*(<\/(?:p|h[1-6]|ul|ol|li|div)[^>]*>)/gi, '$1')
    );
}

export interface SendMessageCallbacks {
    onChatIdUpdate: (oldChatId: string | number, newChatId: string | number) => void;
    onMessageUpdate: (chatId: string | number, messageId: string | number, updates: Partial<ChatMessage>) => void;
    onMessageIdUpdate: (chatId: string | number, oldMessageId: string | number, newMessageId: string | number) => void;
    onStatusMessage: (chatId: string | number, statusId: string, content: string) => void;
    onRemoveStatusMessage: (chatId: string | number, statusId: string) => void;
    onError: (error: string) => void;
    onComplete: () => void;
}

export interface SendMessageParams {
    content: string;
    attachments: File[];
    currentChatId: string | number;
    user?: { user_id?: string | number; username?: string };
    signal?: AbortSignal;
    placeholderMessageId?: string;
}

/**
 * Handles sending a message to Ask Stanley with streaming response
 * Pure business logic - no direct store access
 */
export async function handleSendMessage(params: SendMessageParams, callbacks: SendMessageCallbacks): Promise<void> {
    const { content, attachments, currentChatId, user, signal, placeholderMessageId } = params;
    const {
        onChatIdUpdate,
        onMessageUpdate,
        onMessageIdUpdate,
        onStatusMessage,
        onRemoveStatusMessage,
        onError,
        onComplete,
    } = callbacks;

    let finalChatId = currentChatId;
    let responseChatId: string | number | null = null;
    let responseId: string | number | null = null;
    let accumulatedContent = ''; // Track accumulated response content

    const request: AskStanleyRequest = {
        question: content,
        chat_id: currentChatId,
        user_id: user?.user_id,
        files: attachments,
    };

    try {
        await sendAskStanleyMessage(
            request,
            async (chunk: AskStanleyResponse) => {
                // Handle marker chunk with metadata
                if (chunk.type === 'marker' && chunk.event === 'RunStarted') {
                    responseChatId = chunk.content.chat_id || null;
                    const backendResponseId = chunk.content.response_id || null;

                    // Update chat ID if backend returns a new one
                    if (
                        ((typeof currentChatId === 'number' && currentChatId < 0) ||
                            (typeof currentChatId === 'string' && currentChatId.startsWith('-'))) &&
                        responseChatId !== null
                    ) {
                        finalChatId = responseChatId as string | number;
                        onChatIdUpdate(currentChatId, responseChatId as string | number);
                    }

                    // If we have both placeholder and backend ID, update the message ID from temp to real
                    if (placeholderMessageId && backendResponseId && placeholderMessageId !== backendResponseId) {
                        onMessageIdUpdate(finalChatId, placeholderMessageId, backendResponseId);
                        responseId = backendResponseId;
                    } else {
                        // Fallback: Use placeholder ID if provided, otherwise use backend ID
                        responseId = placeholderMessageId || backendResponseId;
                    }
                }

                // Handle status updates
                if (chunk.type === 'status') {
                    const statusMessageId = ASK_STANLEY_CONSTANTS.MESSAGE_IDS.CURRENT_STATUS;
                    onStatusMessage(finalChatId, statusMessageId, chunk.content);
                }

                // Handle text response
                if (chunk.type === 'response') {
                    // Remove status message when we get actual content
                    const statusMessageId = ASK_STANLEY_CONSTANTS.MESSAGE_IDS.CURRENT_STATUS;
                    onRemoveStatusMessage(finalChatId, statusMessageId);

                    // Accumulate streamed content
                    accumulatedContent += chunk.content;

                    // Update AI message with accumulated content
                    if (responseId) {
                        const rawHtml = await marked.parse(accumulatedContent);
                        const contentHtml = sanitizeHtml(cleanExcessiveBreaks(rawHtml));
                        onMessageUpdate(finalChatId, responseId, {
                            content: accumulatedContent,
                            content_html: contentHtml,
                        });
                    }
                }

                // Handle product data
                if (chunk.type === 'product' && responseId) {
                    onMessageUpdate(finalChatId, responseId, {
                        products: [chunk.content as ProductData],
                    });
                }

                // Handle suggestions
                if (chunk.type === 'suggestions' && responseId) {
                    onMessageUpdate(finalChatId, responseId, {
                        suggestions: chunk.content as string[],
                    });
                }

                // Handle links
                if (chunk.type === 'links' && responseId) {
                    onMessageUpdate(finalChatId, responseId, {
                        links: chunk.content as Array<{ title: string; url: string; description?: string }>,
                    });
                }

                // Handle code blocks
                if (chunk.type === 'code-block' && responseId) {
                    onMessageUpdate(finalChatId, responseId, {
                        codeBlocks: [chunk.content as { language: string; code: string }],
                    });
                }

                // Handle display text
                if (chunk.type === 'display' && responseId) {
                    onMessageUpdate(finalChatId, responseId, {
                        displayText: chunk.content,
                    });
                }
            },
            signal
        );

        // Remove status message on completion
        const statusMessageId = ASK_STANLEY_CONSTANTS.MESSAGE_IDS.CURRENT_STATUS;
        onRemoveStatusMessage(finalChatId, statusMessageId);
        onComplete();
    } catch (error: any) {
        if (error.name === 'AbortError') {
            console.error('Request aborted by user');
            return;
        }

        console.error('Error sending message:', error);
        onError(error.message || 'Failed to send message. Please try again.');

        // Remove any status messages
        const statusMessageId = ASK_STANLEY_CONSTANTS.MESSAGE_IDS.CURRENT_STATUS;
        onRemoveStatusMessage(finalChatId, statusMessageId);
        onComplete();

        throw error;
    }
}
